import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { C, PORTRAIT, DEFAULT_STATS, media, useContent } from "./theme";

function parseStat(s) {
  const raw = String(s.value ?? `${s.end ?? ""}${s.suffix ?? ""}`);
  const num = parseFloat(raw) || 0;
  const decimals = ((raw.match(/\.(\d+)/) || [])[1] || "").length;
  return { num, decimals, suffix: raw.replace(/^[\d.]+/, ""), label: s.label };
}

function Stat({ stat }) {
  const { num, decimals, suffix, label } = parseStat(stat);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / 1600);
      setVal(num * (1 - Math.pow(1 - t, 3)));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, num]);
  return (
    <div ref={ref} data-testid="about-stat">
      <p className="ed-serif font-light text-2xl xl:text-4xl leading-none tabular-nums" style={{ color: C.burgundy }}>{val.toFixed(decimals)}{suffix}</p>
      <p className="ed-mono text-[7px] xl:text-[9px] uppercase tracking-[0.18em] mt-1.5" style={{ color: C.muted }}>{label}</p>
    </div>
  );
}

function DataField({ k, v }) {
  return (
    <div className="min-w-0">
      <p className="ed-mono text-[7px] xl:text-[8px] uppercase tracking-[0.25em]" style={{ color: C.muted }}>{k}</p>
      <p className="ed-mono text-[11px] xl:text-[13px] uppercase tracking-[0.08em] truncate" style={{ color: C.wine }}>{v}</p>
    </div>
  );
}

function PassportPhoto({ src, focus, zoom }) {
  return (
    <div className="relative flex-shrink-0 w-[118px] xl:w-[140px] aspect-[3/4] overflow-hidden rounded-sm" style={{ border: `1px solid ${C.burgundy}33` }} data-testid="hero-portrait">
      <img src={media(src) || PORTRAIT} alt="Shruti Aggarwal" className="w-full h-full object-cover" style={{ objectPosition: focus, transform: `scale(${zoom})`, transformOrigin: focus, filter: "sepia(0.12) contrast(1.04)" }} />
      <motion.span
        className="absolute inset-0 pointer-events-none mix-blend-screen"
        animate={{ backgroundPosition: ["0% 0%", "100% 100%"] }}
        transition={{ duration: 6, repeat: Infinity, repeatType: "reverse", ease: "linear" }}
        style={{ backgroundImage: `linear-gradient(120deg, transparent 30%, ${C.blush}55 45%, ${C.goldLight}44 55%, transparent 70%)`, backgroundSize: "250% 250%" }}
      />
      <span className="absolute -bottom-3 -right-3 w-14 h-14 rounded-full" style={{ border: `1.5px solid ${C.rose}88` }} />
    </div>
  );
}

export function IntroPage() {
  const i = useContent("intro");
  const st = useContent("stats");
  const stats = st.items?.length ? st.items : DEFAULT_STATS;
  const fields = [
    ["Surname", i.surname || "Aggarwal"],
    ["Given names", i.givenNames || "Shruti"],
    ["Profession", i.profession || "Brand strategist"],
    ["Place of issue", i.placeOfIssue || "New York"],
    ["Date of issue", i.dateOfIssue || "Dec 2026"],
    ["Valid for", i.validFor || "Brand · Strategy · Search · PMM"],
  ];
  const mrz = `P<SA${(i.surname || "Aggarwal").toUpperCase()}<<${(i.givenNames || "Shruti").toUpperCase()}<<BRAND<STRATEGY`.padEnd(44, "<").slice(0, 44);
  return (
    <div id="hero" data-testid="clean-hero" className="relative lg:h-full px-6 md:px-12 xl:px-14 py-8 xl:py-8 flex flex-col lg:justify-center overflow-hidden"
>
      <div className="flex items-baseline justify-between">
        <p className="ed-mono text-[9px] md:text-[10px] uppercase tracking-[0.35em]" style={{ color: C.gold }}>{i.eyebrow || "p.02 · Holder's page"}</p>
        <p className="ed-mono text-[9px] uppercase tracking-[0.3em] hidden sm:block" style={{ color: C.muted }}>Type P · Code SA</p>
      </div>
      <h2 className="ed-serif font-light leading-[0.98] tracking-[-0.015em] mt-4 xl:mt-6" style={{ color: C.wine, fontSize: "clamp(1.6rem, 2.5vw, 3.1rem)" }}>
        {i.nameLine || "I'm Shruti."} <em style={{ color: C.rose }}>{i.titleLine || "A brand strategist with a finance brain."}</em>
      </h2>
      <div className="flex gap-5 xl:gap-8 mt-4 xl:mt-5">
        <PassportPhoto src={i.portrait} focus={i.photoFocus || (i.portrait ? "50% 25%" : "13% 76%")} zoom={parseFloat(i.photoZoom) || (i.portrait ? 1.4 : 2.6)} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2 xl:gap-y-3 content-start flex-1 min-w-0">
          {fields.map(([k, v]) => <DataField key={k} k={k} v={v} />)}
          <p className="sm:col-span-2 ed-script text-2xl xl:text-3xl -mt-1" style={{ color: C.burgundy }}>Shruti A.</p>
        </div>
      </div>
      <p className="mt-4 xl:mt-5 text-[14px] xl:text-[15px] leading-[1.65] max-w-xl" style={{ color: C.rich }}>
        {i.body || "Two years of forensic finance at Stout taught me to read a balance sheet, value a Fortune 500, and trust the data. Now I bring that rigor to brand strategy, search, and the work of making people actually care."}
      </p>
      <div className="grid grid-cols-4 gap-3 mt-4 xl:mt-5 pt-3 max-w-xl" style={{ borderTop: `1px solid ${C.burgundy}1f` }} data-testid="about-stats">
        {stats.slice(0, 4).map((s, k) => <Stat key={k} stat={s} />)}
      </div>
      <p className="ed-mono text-[9px] xl:text-[10px] tracking-[0.2em] mt-4 truncate opacity-70" style={{ color: C.wine }} aria-hidden>{mrz}</p>
    </div>
  );
}
